'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  subject: string;
  enquiryType: string;
  message: string;
  gdprConsent: boolean;
}

interface FormErrors {
  [key: string]: string;
}

const GeneralEnquiryForm = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    enquiryType: '',
    message: '',
    gdprConsent: false
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const enquiryTypes = [
    'General Information',
    'Partnership Opportunity',
    'Media Enquiry',
    'Speaking Engagement',
    'Career Opportunity',
    'Technical Support',
    'Feedback',
    'Other'
  ];

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData.enquiryType) {
      newErrors.enquiryType = 'Please select enquiry type';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Message must be at least 20 characters';
    }

    if (!formData.gdprConsent) {
      newErrors.gdprConsent = 'You must consent to data processing';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    setFormData({
      fullName: '',
      email: '',
      phone: '',
      company: '',
      subject: '',
      enquiryType: '',
      message: '',
      gdprConsent: false
    });

    setTimeout(() => {
      setSubmitSuccess(false);
    }, 5000);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-lg shadow-card p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-10 bg-muted rounded"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div className="bg-card rounded-lg shadow-card p-8 lg:p-12">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-success/10 rounded-full mb-6">
            <Icon name="CheckCircleIcon" size={40} variant="solid" className="text-success" />
          </div>
          <h3 className="text-2xl font-bold font-headline text-primary mb-4">
            Message Sent Successfully!
          </h3>
          <p className="text-lg text-muted-foreground font-body mb-6">
            Thank you for contacting AI Change Leaders. We've received your enquiry and will respond within 24 hours during business days.
          </p>
          <div className="bg-primary/5 rounded-lg p-6 mb-8">
            <h4 className="font-semibold font-headline text-primary mb-3">What happens next?</h4>
            <ul className="space-y-2 text-left">
              <li className="flex items-start gap-2">
                <Icon name="EnvelopeIcon" size={20} variant="outline" className="text-secondary mt-0.5" />
                <span className="text-sm text-foreground font-body">Confirmation email sent to your inbox</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="UserIcon" size={20} variant="outline" className="text-secondary mt-0.5" />
                <span className="text-sm text-foreground font-body">Assigned to appropriate team member</span>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="ClockIcon" size={20} variant="outline" className="text-secondary mt-0.5" />
                <span className="text-sm text-foreground font-body">Response within 24 hours (business days)</span>
              </li>
            </ul>
          </div>
          <button
            onClick={() => setSubmitSuccess(false)}
            className="px-6 py-3 rounded-md font-semibold font-cta text-sm text-secondary-foreground bg-secondary hover:bg-secondary/90 transition-all duration-300"
          >
            Send Another Message
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-card p-8 lg:p-12">
      <div className="mb-8">
        <h3 className="text-2xl font-bold font-headline text-primary mb-3">
          General Enquiry
        </h3>
        <p className="text-muted-foreground font-body">
          Have a question or want to learn more? Fill out the form below and we'll get back to you promptly.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="fullName" className="block text-sm font-semibold font-body text-foreground mb-2">
              Full Name <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.fullName ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
              placeholder="Michael Brown"
            />
            {errors.fullName && (
              <p className="mt-1 text-sm text-error font-body">{errors.fullName}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold font-body text-foreground mb-2">
              Email Address <span className="text-error">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.email ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
              placeholder="michael@company.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-error font-body">{errors.email}</p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="phone" className="block text-sm font-semibold font-body text-foreground mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
              placeholder="+44 20 1234 5678"
            />
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-semibold font-body text-foreground mb-2">
              Company
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300"
              placeholder="Tech Innovations Ltd"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="subject" className="block text-sm font-semibold font-body text-foreground mb-2">
              Subject <span className="text-error">*</span>
            </label>
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.subject ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
              placeholder="Brief description of your enquiry"
            />
            {errors.subject && (
              <p className="mt-1 text-sm text-error font-body">{errors.subject}</p>
            )}
          </div>

          <div>
            <label htmlFor="enquiryType" className="block text-sm font-semibold font-body text-foreground mb-2">
              Enquiry Type <span className="text-error">*</span>
            </label>
            <select
              id="enquiryType"
              name="enquiryType"
              value={formData.enquiryType}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-md border ${
                errors.enquiryType ? 'border-error' : 'border-input'
              } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300`}
            >
              <option value="">Select enquiry type</option>
              {enquiryTypes.map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            {errors.enquiryType && (
              <p className="mt-1 text-sm text-error font-body">{errors.enquiryType}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-semibold font-body text-foreground mb-2">
            Message <span className="text-error">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows={6}
            className={`w-full px-4 py-3 rounded-md border ${
              errors.message ? 'border-error' : 'border-input'
            } bg-background text-foreground font-body focus:outline-none focus:ring-2 focus:ring-ring transition-all duration-300 resize-none`}
            placeholder="Please provide details about your enquiry..."
          />
          {errors.message && (
            <p className="mt-1 text-sm text-error font-body">{errors.message}</p>
          )}
        </div>

        <div className="bg-muted/50 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="gdprConsent"
              name="gdprConsent"
              checked={formData.gdprConsent}
              onChange={handleChange}
              className="mt-1 w-5 h-5 rounded border-input text-secondary focus:ring-2 focus:ring-ring"
            />
            <label htmlFor="gdprConsent" className="text-sm text-foreground font-body">
              I consent to AI Change Leaders processing my personal data to respond to my enquiry in accordance with GDPR. I understand I can withdraw consent at any time. <span className="text-error">*</span>
            </label>
          </div>
          {errors.gdprConsent && (
            <p className="mt-2 text-sm text-error font-body ml-8">{errors.gdprConsent}</p>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex-1 px-8 py-4 rounded-md font-semibold font-cta text-base text-secondary-foreground bg-secondary hover:bg-secondary/90 disabled:bg-muted disabled:text-muted-foreground transition-all duration-300 shadow-subtle hover:shadow-strong hover:-translate-y-0.5 disabled:hover:translate-y-0 disabled:hover:shadow-subtle"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                Sending Message...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Icon name="PaperAirplaneIcon" size={20} variant="outline" />
                Send Message
              </span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GeneralEnquiryForm;